/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Asking a mapping editor to open, or reveal, one row** (`#1305`).
 *
 * A finding in the source validation panel names a field — a parameter, a header, a body path, a
 * response target, a pixel. Its affordance was ratified by Todd on 2026-09-04 as two acts rather
 * than one:
 *
 * | | Means | The system knows |
 * |---|---|---|
 * | **Fix** | stage the change **and open the editor for that field** | *what the change is* |
 * | **Review** | scroll to the field | *where the problem is, not what to do* |
 *
 * Both halves landed at the **section**: the operator arrived in the right editor and then had to
 * find the row themselves, which on a forty-row parameter list or an eight-section ORTB body is the
 * part that was supposed to be done for them.
 *
 * ## Why this is one declaration rather than six fixes
 *
 * Six editors own their row-opening state privately and key their rows three different ways — by
 * generated id (parameters, headers, the round plan's override editor), by **index** (response
 * mapping), by a **path label** (the ORTB body), and by a collapse map (pixels). `#1262` established
 * the shape for exactly this set: what the surfaces share is the **rule**, and each answers a
 * **required** declaration saying how it finds the row. A prop that renders nothing when forgotten
 * needs a type rather than a test (`#1134`), and *pointable at a row* is one of those: an editor
 * that silently ignored the request would look like one nobody had pointed at.
 *
 * ## The two intents are not two degrees of the same thing
 *
 * `open` expands the row for editing — it is *Fix*'s verb, and the operator lands on the staged
 * change with the control that produced it under their cursor. `reveal` scrolls to the row and
 * leaves it exactly as it is — it is *Review*'s verb, and expanding a row the system has no remedy
 * for would claim an act it cannot make. Getting this backwards is how *Fix* and *Review* come to
 * mean the same thing on screen while reading as two words.
 */

import React from 'react';
import { EXPRESSION_LOCATION_ATTRIBUTE, encodeLocation, type ExpressionLocation } from './expression-walk';

/**
 * **Which editor a request is for**, and the same vocabulary a finding records as its origin
 * (`SourceIssue.origin`) — one name per concept, so nothing maps between the two.
 *
 * A section holds more than one editor: `http` holds headers and parameters, `responseMapping`
 * holds the response mapping and the pixels. Matching on the ref alone would let a parameter named
 * `ip` open a header of the same name.
 */
/*
 * `roundOverride` left with the override modal (`#852` slice 4b). It was declared and answered with no
 * caller, since the round plan has no findings panel; a round's change list asks for a scope when one does.
 */
export type RowOpenScope = 'headers' | 'params' | 'ortbBody' | 'responseMapping' | 'pixels';

export interface RowOpenRequest {
  /** The editor being asked. An editor ignores a request that is not its own. */
  scope: RowOpenScope;
  /** The row, in the vocabulary the finding used — a name, a path, a target, a pixel label. */
  ref: string;
  /** `open` expands the row; `reveal` scrolls to it and changes nothing. */
  intent: 'open' | 'reveal';
  /**
   * Distinguishes two requests for the **same** row, which is an ordinary case — two Fixes on one
   * finding. Without it the second request has the same token as the first and does nothing.
   */
  requestId: number;
  /**
   * **A field inside the row's expression** (`4c`, `#1334`), or absent for the row itself. Only
   * meaningful with `intent: 'open'`: the field is drawn only while its row is open.
   */
  at?: ExpressionLocation;
}

/**
 * The DOM id a row carries so it can be revealed without being opened.
 *
 * A ref is the editor's handle on a row it renders; this is a **reveal that must not change what
 * is open**, so there is nothing to hang a ref on — the same reason `useRevealAnchor` exists beside
 * `useRevealOnOpen`. Scoped because two editors in one section legitimately hold a row of the same
 * name.
 */
export function rowAnchorId(scope: RowOpenScope, ref: string): string {
  return `row-${scope}-${ref.trim().replace(/[^A-Za-z0-9_.-]+/g, '_')}`;
}

/**
 * Scroll to a DOM anchor once it appears, retrying a bounded handful of frames, and **say so** when
 * it never does. Returns a cancel.
 *
 * Extracted from `useRevealAnchor` (`#1254`) when the row reveal became its second caller. The
 * retry is not defensive padding: the element is routinely rendered by the commit that requested
 * the reveal, and a section remounting under a nonce genuinely is not there on the first frame.
 * `?.scrollIntoView` swallows both cases identically, which is why the failure is reported rather
 * than absorbed — to the console, since a sentence about a missing DOM id names a mechanism rather
 * than describing an operator's outcome (`#1045`).
 */
export function revealAnchorWhenPresent(anchorId: string, block: ScrollLogicalPosition): () => void {
  let frame = 0;
  let attempts = 0;
  // Six frames is about a tenth of a second — long enough for a remount under a nonce and an
  // expansion, short enough that a genuinely absent anchor is reported rather than waited on.
  const MAX_ATTEMPTS = 6;
  const tryReveal = () => {
    const element = document.getElementById(anchorId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block });
      return;
    }
    if (++attempts >= MAX_ATTEMPTS) {
      console.warn(`[reveal] no element with id "${anchorId}" appeared — the jump to it did nothing.`);
      return;
    }
    frame = requestAnimationFrame(tryReveal);
  };
  frame = requestAnimationFrame(tryReveal);
  return () => cancelAnimationFrame(frame);
}

/**
 * **Scroll to a field inside an opened row's expression** (`4c`, `#1334`) — the deepest part of the
 * location the editor actually drew, since a location can be deeper than it will draw
 * (`MAX_CONDITIONAL_NESTING`) or inside a condition list it cannot show. Returns a cancel.
 *
 * Found **within the row**, by the `data-expr-at` the expression editor stamps at each location,
 * rather than by a page-wide id: the editor does not know which row it is in, and should not have to
 * — the row already carries its anchor.
 *
 * A **field** at the end of the location takes focus as well as the scroll, so the operator lands
 * where they type (pattern 38). A case, which is a box of fields, only scrolls: focusing its first
 * input would put the cursor in a field the finding is not about.
 */
export function revealExpressionLocationWhenPresent(rowAnchor: string, at: ExpressionLocation): () => void {
  let frame = 0;
  let attempts = 0;
  const MAX_ATTEMPTS = 6;
  const find = (row: HTMLElement): { element: HTMLElement; complete: boolean } | null => {
    for (let length = at.length; length > 0; length -= 1) {
      const token = encodeLocation(at.slice(0, length));
      const element = row.querySelector<HTMLElement>(`[${EXPRESSION_LOCATION_ATTRIBUTE}="${token}"]`);
      if (element) return { element, complete: length === at.length };
    }
    return null;
  };
  const tryReveal = () => {
    const row = document.getElementById(rowAnchor);
    const found = row ? find(row) : null;
    // Keep waiting for the whole location while the editor may still be drawing; settle for the
    // deepest part of it once it has had the frames a remount needs.
    if (found && (found.complete || attempts + 1 >= MAX_ATTEMPTS)) {
      found.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const last = at[at.length - 1];
      if (found.complete && last.kind !== 'case' && last.kind !== 'condition') {
        found.element.querySelector<HTMLElement>('input, textarea, select')?.focus({ preventScroll: true });
      }
      return;
    }
    if (++attempts >= MAX_ATTEMPTS) {
      console.warn(
        `[reveal] no field "${encodeLocation(at)}" appeared in "${rowAnchor}" — the jump to it did nothing.`,
      );
      return;
    }
    frame = requestAnimationFrame(tryReveal);
  };
  /**
   * **The first look is a frame late, on purpose.** Opening the row makes the editor's own
   * `useRevealOnOpen` queue a scroll to the **row** in the same commit as this request, and frames run
   * their callbacks in the order they were queued — so starting on the first frame put this scroll
   * first and the row's second, and the operator ended at the top of the row. Found by
   * `expression-location.test.tsx`, which asserts the scroll the operator ends on.
   *
   * This is ordering against a scroll queued in the same commit, not a guess at React's timing
   * (pattern 37): the retries below still wait for the field itself.
   */
  frame = requestAnimationFrame(() => {
    frame = requestAnimationFrame(tryReveal);
  });
  return () => cancelAnimationFrame(frame);
}

/**
 * Honour a row-open request in this editor.
 *
 * @param scope    what this editor is. A request for another scope is not this editor's business.
 * @param request  the page's standing request, or `null` where nothing can point at this surface.
 * @param resolve  **this editor's own answer to _which row is that_** — the required declaration.
 *                 `null` when it holds no such row, which is the normal case for the sibling editor
 *                 in the same section. It is also where an editor makes the row **reachable**: the
 *                 ORTB body renders neither the row nor its anchor inside a collapsed section, so
 *                 resolving expands it. That applies to both intents, which is why it belongs here
 *                 and not in `open`.
 * @param open     expand that row for editing. Called only for `intent: 'open'`.
 *
 * **It fires on mount, and that is the deliberate difference from `useRevealOnOpen`.** That hook
 * refuses to scroll on a first render because a surface opening with something already expanded
 * must not yank the page — an *action* earns a scroll. Here the mount **is** the action: the
 * section remounts under its Fix nonce, so a request that did nothing on mount would do nothing at
 * all.
 */
export function useRowOpenRequest<K>(
  scope: RowOpenScope,
  request: RowOpenRequest | null,
  resolve: (ref: string) => K | null,
  open: (key: K) => void,
): void {
  const mine = request && request.scope === scope ? request : null;
  const token = mine ? `${mine.intent}:${mine.ref}@${encodeLocation(mine.at ?? [])}#${mine.requestId}` : null;
  const previous = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (token === null || token === previous.current || !mine) return;
    previous.current = token;
    const key = resolve(mine.ref);
    // A finding names a row the sibling editor owns, or one that has since been deleted. Neither is
    // an error: the section is open and the staged change, if any, has landed regardless.
    if (key === null) return;
    if (mine.intent === 'open') {
      open(key);
      // The reveal follows from the row opening — `useRevealOnOpen` is already watching the key
      // that just changed. A second scroll here would fight it.
      //
      // **Except where the finding is inside the expression** (`#1334`): the row's reveal lands on
      // the row, and a ladder of four cases is exactly the hunt pattern 38 exists to end. This
      // scroll runs frames later, once the opened row has drawn its editor, and so supersedes it.
      if (mine.at && mine.at.length > 0)
        return revealExpressionLocationWhenPresent(rowAnchorId(scope, mine.ref), mine.at);
      return;
    }
    return revealAnchorWhenPresent(rowAnchorId(scope, mine.ref), 'nearest');
    // **The token is the whole dependency**, as it is in `useRevealAnchor` next door. `resolve` and
    // `open` are fresh closures on every render, so listing them would re-run this on every
    // keystroke in the editor — and the closure captured by the render that changed the token is
    // the one that describes the rows as they are now.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
}

/**
 * **Review from the editor's own finding list** — what `ValidationSummary` offers a finding it has
 * nothing to apply to.
 *
 * The page panel's Review has been a reveal request since `#1305`; the in-editor list, which renders
 * where there is no panel (the wizard), said *no auto-fix* on the same findings. So this is that same
 * request raised locally — one mechanism, and each editor's existing lookup answers it.
 */
export interface FindingReview {
  /**
   * Whether this editor holds the row a finding names. **Pure**, because it is asked while
   * rendering: a finding naming no row — a field not yet added, a sibling editor's row — must not
   * offer a Review that goes nowhere, which would be a live control that does nothing.
   */
  reaches: (ref: string) => boolean;
  /** Scroll to that row, expanding nothing. */
  go: (ref: string) => void;
  /**
   * **Open that row at a field inside its expression** (`#1334`) — the in-editor twin of the page
   * panel's Fix for the same finding, which a reveal could not reach because the field is drawn
   * only while the row is open.
   */
  openAt: (ref: string, at: ExpressionLocation) => void;
}

/**
 * @param resolve the same lookup this editor hands `useRowOpenRequest` — it may make the row
 *                reachable (the ORTB body expands a collapsed section), which is why it is not also
 *                used to answer `reaches`.
 * @param reaches this editor's pure answer to *do I hold that row*.
 */
export function useFindingReview<K>(
  scope: RowOpenScope,
  resolve: (ref: string) => K | null,
  reaches: (ref: string) => boolean,
  /** The same `open` this editor hands `useRowOpenRequest` — called only by {@link FindingReview.openAt}. */
  open: (key: K) => void,
): FindingReview {
  const [request, setRequest] = React.useState<RowOpenRequest | null>(null);
  useRowOpenRequest(scope, request, resolve, open);
  return {
    reaches,
    go: (ref) =>
      // A fresh id each time, so reviewing the same finding twice scrolls twice.
      setRequest((prev) => ({ scope, ref, intent: 'reveal', requestId: (prev?.requestId ?? 0) + 1 })),
    openAt: (ref, at) =>
      setRequest((prev) => ({ scope, ref, intent: 'open', at, requestId: (prev?.requestId ?? 0) + 1 })),
  };
}
